// adminLogsService.ts
import api from '@/services/api'

export interface AuditLogEntry {
  id: number
  action: string
  details: any
  ipAddress: string
  userAgent?: string
  createdAt: string
  adminUser: {
    id: number
    firstName: string
    lastName: string
    email: string
    role: string
  }
  targetUser?: {
    id: number
    firstName: string
    lastName: string
    email: string
  }
  targetEmployee?: {
    id: number
    employeeNumber: string
    firstName: string
    lastName: string
  }
}

export interface SystemLogEntry {
  id: number
  level: string
  message: string
  context: any
  timestamp: string
  source: string
  userId?: number
}

export interface SystemLogsResponse {
  logs: SystemLogEntry[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface LogsResponse {
  auditLogs: AuditLogEntry[]
  systemLogs: SystemLogsResponse
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface LogsQueryParams {
  page?: number
  limit?: number
  type?: 'audit' | 'system'
  search?: string
  level?: string
  startDate?: string
  endDate?: string
}

class AdminLogsService {
  async getLogs(params: LogsQueryParams): Promise<LogsResponse> {
    const queryParams = new URLSearchParams()
    
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.type) queryParams.append('type', params.type)
    if (params.search) queryParams.append('search', params.search)
    if (params.level) queryParams.append('level', params.level)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)

    const response = await api.get(`/admin/logs?${queryParams}`)
    return response.data.data
  }

  async getAuditLogDetails(logId: number): Promise<AuditLogEntry> {
    const response = await api.get(`/admin/logs/${logId}`)
    return response.data.data
  }

  async getUserAuditHistory(userId: number, page: number = 1, limit: number = 10) {
    const response = await api.get(`/admin/logs/user/${userId}/history?page=${page}&limit=${limit}`)
    return response.data.data
  }

  async exportLogs(params: LogsQueryParams): Promise<Blob> {
    const queryParams = new URLSearchParams()
    
    if (params.type) queryParams.append('type', params.type)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)

    const response = await api.get(`/admin/logs/export/csv?${queryParams}`, {
      responseType: 'blob'
    })
    
    return response.data
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  getActionLabel(action: string): string {
    const actionLabels: Record<string, string> = {
      'USER_LOGIN': 'User Login',
      'USER_LOGOUT': 'User Logout',
      'PASSWORD_RESET': 'Password Reset',
      'CREDENTIALS_VIEWED': 'Credentials Viewed',
      'USER_ACTIVATED': 'Account Activated',
      'USER_DEACTIVATED': 'Account Deactivated',
      'ROLE_UPDATED': 'Role Updated',
      'FORCE_PASSWORD_RESET': 'Force Password Reset',
      'BULK_USER_ACTIVATED': 'Bulk Activation',
      'BULK_USER_DEACTIVATED': 'Bulk Deactivation',
      'BULK_FORCE_PASSWORD_RESET': 'Bulk Force Password Reset',
      'VIEW_USER_AUDIT_HISTORY': 'Audit History Viewed',
      'REVOKE_PASSWORD_RESET_TOKENS': 'Password Reset Tokens Revoked',
      'EMPLOYEE_CREATED': 'Employee Created',
      'EMPLOYEE_UPDATED': 'Employee Updated',
      'EMPLOYEE_DELETED': 'Employee Deleted',
      'PAYROLL_RUN': 'Payroll Processed',
      'EXCHANGE_RATE_UPDATED': 'Exchange Rate Updated'
    }

    return actionLabels[action] || action
  }

  getActionColor(action: string): string {
    switch (action) {
      case 'USER_LOGIN':
      case 'USER_LOGOUT':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
      case 'PASSWORD_RESET':
      case 'FORCE_PASSWORD_RESET':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30'
      case 'USER_ACTIVATED':
      case 'EMPLOYEE_CREATED':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30'
      case 'USER_DEACTIVATED':
      case 'EMPLOYEE_DELETED':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
      case 'ROLE_UPDATED':
      case 'EMPLOYEE_UPDATED':
        return 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30'
      case 'CREDENTIALS_VIEWED':
        return 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30'
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
    }
  }

  getLevelColor(level: string): string {
    switch (level) {
      case 'ERROR':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
      case 'WARNING':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30'
      case 'INFO':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
      case 'DEBUG':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
    }
  }
}

export const adminLogsService = new AdminLogsService()
export default adminLogsService